import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { QrCode, X, ShoppingBag, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

export const QRScanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanner, setScanner] = useState(null);

  useEffect(() => {
    let html5QrcodeScanner = null;

    const initScanner = async () => {
      if (isOpen) {
        // Wait for DOM to be ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const element = document.getElementById('qr-reader');
        if (element && !scanner) {
          const onScanSuccessCallback = (decodedText, decodedResult) => {
            console.log(`QR Code scanned: ${decodedText}`);
            setScanResult(decodedText);
            
            // Redirect to WhatsApp with the scanned data
            handleWhatsAppRedirect(decodedText);
          };

          const onScanFailureCallback = (error) => {
            // Handle scan failure silently
          };

          html5QrcodeScanner = new Html5QrcodeScanner(
            'qr-reader',
            { 
              fps: 10, 
              qrbox: { width: 250, height: 250 },
              aspectRatio: 1.0
            },
            false
          );

          html5QrcodeScanner.render(onScanSuccessCallback, onScanFailureCallback);
          setScanner(html5QrcodeScanner);
        }
      }
    };

    initScanner();

    return () => {
      if (scanner) {
        scanner.clear().catch(error => {
          console.error('Failed to clear scanner:', error);
        });
      }
    };
  }, [isOpen]);

  const onScanSuccess = (decodedText, decodedResult) => {
    console.log(`QR Code scanned: ${decodedText}`);
    setScanResult(decodedText);
    
    // Redirect to WhatsApp with the scanned data
    handleWhatsAppRedirect(decodedText);
  };

  const onScanFailure = (error) => {
    // Handle scan failure silently
  };

  const handleWhatsAppRedirect = (qrData) => {
    const whatsappNumber = '919560209663';
    const message = `Hello! I'd like to order food. \n\nScanned QR Code: ${qrData}\n\nPlease help me with my order.`;
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Close the scanner dialog
    setTimeout(() => {
      setIsOpen(false);
      setScanResult(null);
    }, 1000);
  };

  const handleOpenScanner = () => {
    setIsOpen(true);
    setScanResult(null);
  };

  const handleCloseScanner = () => {
    if (scanner) {
      scanner.clear();
      setScanner(null);
    }
    setIsOpen(false);
    setScanResult(null);
  };

  return (
    <>
      {/* Floating QR Scanner Button */}
      <div className="fixed bottom-8 right-8 z-40">
        <Button
          onClick={handleOpenScanner}
          className="bg-[#FE9E1C] hover:bg-[#e68a0f] text-white rounded-full w-16 h-16 shadow-2xl hover:scale-110 transition-all duration-300 flex items-center justify-center group"
          aria-label="Scan QR Code for Food Ordering"
        >
          <QrCode className="w-8 h-8 group-hover:rotate-12 transition-transform" strokeWidth={2} />
        </Button>
        
        {/* Tooltip */}
        <div className="absolute bottom-20 right-0 bg-[#59642C] text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          Scan QR to Order
          <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#59642C]"></div>
        </div>
      </div>

      {/* Scanner Modal */}
      <Dialog open={isOpen} onOpenChange={handleCloseScanner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-3 text-2xl text-[#59642C]">
              <QrCode className="w-7 h-7 text-[#FE9E1C]" />
              <span>Scan QR Code to Order</span>
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Point your camera at the QR code to start ordering via WhatsApp
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* QR Scanner Container */}
            <div className="relative">
              <div 
                id="qr-reader" 
                className="rounded-2xl overflow-hidden border-4 border-[#FE9E1C]/20"
              ></div>
              
              {scanResult && (
                <div className="absolute inset-0 bg-[#59642C]/95 flex flex-col items-center justify-center rounded-2xl">
                  <div className="w-16 h-16 bg-[#FE9E1C] rounded-full flex items-center justify-center mb-4">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-white text-lg font-bold mb-2">QR Code Scanned!</p>
                  <p className="text-white/80 text-sm">Redirecting to WhatsApp...</p>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-gradient-to-br from-[#EBEBD3]/30 to-white p-4 rounded-xl border border-[#EBEBD3]">
              <div className="flex items-start space-x-3">
                <ShoppingBag className="w-5 h-5 text-[#59642C] mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  <p className="font-semibold text-[#59642C] mb-1">How to order:</p>
                  <ol className="list-decimal list-inside space-y-1 text-gray-600">
                    <li>Scan the QR code on your table or menu</li>
                    <li>WhatsApp will open automatically</li>
                    <li>Chat with our agent to place your order</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <Button
              onClick={handleCloseScanner}
              variant="outline"
              className="w-full border-[#59642C] text-[#59642C] hover:bg-[#59642C] hover:text-white transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
